import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { BaseChannel, User } from "@sendbird/chat"
import {
  GroupChannel,
  GroupChannelCollection,
  GroupChannelCollectionEventHandler,
  GroupChannelEventContext,
  GroupChannelFilter,
  GroupChannelListOrder,
  MessageCollection,
  MessageCollectionEventHandler,
  MessageCollectionInitPolicy,
  MessageFilter,
  UnreadChannelFilter,
} from "@sendbird/chat/groupChannel"
import { BaseMessage, UserMessage } from "@sendbird/chat/message"

import { useAppSelector } from "app/hooks"
import useToastHook from "hooks/useToastHook/useToastHook"
import { createChannel, sendbirdChat } from "services/sendbird/sendbird"
import { selectUser } from "store/auth/authSlice"

type SendbirdProviderProps = {
  children: ReactNode
}

interface LoadMessagesParams {
  channel: GroupChannel
  messageHandler: MessageCollectionEventHandler
  onCacheResult: (err: Error | null, messages: UserMessage[] | null) => void
  onApiResult: (err: Error | null, messages: UserMessage[] | null) => void
}

interface SendbirdDataState {
  sendBirdUser: User | null
  loading: boolean
  connectedToChat: boolean
  groupChannelCollection: GroupChannelCollection | null
  channels: GroupChannel[] | null
  currentlyJoinedChannel: GroupChannel | null
  messagesLoading: boolean
  messageCollection: MessageCollection | null
  messages: UserMessage[]
  unreadCount: number
}
interface LoadChannelsArgs {
  channelNameContainsFilter?: string
  unreadChannelFilter?: boolean
}
interface SendbirdContextState extends SendbirdDataState {
  loadChannels: ({
    channelNameContainsFilter,
    unreadChannelFilter,
  }: LoadChannelsArgs) => void
  handleOpenChannel: (channelUrl: string) => void
  sendMessage: (messageValue: string) => void
  handleCreateChannel: (channelName: string, userIdsToInvite: string[]) => void
  startTyping: () => void
  endTyping: () => void
  resetCurrentlyJoinedChannel: () => void
}

export const SendbirdContext = createContext<SendbirdContextState | undefined>(
  undefined,
)

const SendbirdProvider = (props: SendbirdProviderProps) => {
  const { children } = props
  const displayToast = useToastHook()

  const [state, setState] = useState<SendbirdDataState>({
    sendBirdUser: null,
    loading: false,
    connectedToChat: false,
    groupChannelCollection: null,
    channels: null,
    currentlyJoinedChannel: null,
    messagesLoading: false,
    messageCollection: null,
    messages: [],
    unreadCount: 0,
  })

  // need to access state in message received callback
  const stateRef = useRef<SendbirdDataState>(state)
  stateRef.current = state

  const currentUser = useAppSelector(selectUser)
  const channelsRef = useRef<GroupChannel[]>()
  channelsRef.current = state.channels || []

  const handleUnreadMsg = async () => {
    const unreadCount =
      await sendbirdChat.groupChannel.getTotalUnreadMessageCount()
    setState((prevState) => ({
      ...prevState,
      unreadCount: unreadCount || 0,
    }))
  }

  const loadChannels = useCallback(
    ({
      channelNameContainsFilter = "",
      unreadChannelFilter = false,
    }: LoadChannelsArgs) => {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }))
      const channelHandlers: GroupChannelCollectionEventHandler = {
        onChannelsAdded: (
          _context: GroupChannelEventContext,
          channels: BaseChannel[],
        ) => {
          const updatedChannels = [...channels, ...(channelsRef.current || [])]
          setState((prevState) => ({
            ...prevState,
            channels: updatedChannels as GroupChannel[],
          }))
          // dispatch(setChannels(updatedChannels as GroupChannel[]))
        },
        onChannelsDeleted: (
          _context: GroupChannelEventContext,
          channelUrls: string[],
        ) => {
          const updatedChannels = channelsRef?.current?.filter(
            (channel) => !channelUrls.includes(channel.url),
          )
          setState((prevState) => ({
            ...prevState,
            channels: updatedChannels as GroupChannel[],
          }))
        },
        onChannelsUpdated: (
          _context: GroupChannelEventContext,
          channels: BaseChannel[],
        ) => {
          const updatedChannels = channelsRef?.current?.map((channel) => {
            const updatedChannel = channels.find(
              (incomingChannel) => incomingChannel.url === channel.url,
            )
            if (updatedChannel) {
              return updatedChannel
            }
            return channel
          })
          // eslint-disable-next-line no-void
          void handleUnreadMsg()
          setState((prevState) => ({
            ...prevState,
            channels: updatedChannels as GroupChannel[],
          }))
        },
      }

      const groupChannelFilter = new GroupChannelFilter()
      groupChannelFilter.includeEmpty = true
      groupChannelFilter.channelNameContainsFilter = channelNameContainsFilter
      if (unreadChannelFilter) {
        groupChannelFilter.unreadChannelFilter =
          UnreadChannelFilter.UNREAD_MESSAGE
      }

      const collection = sendbirdChat.groupChannel.createGroupChannelCollection(
        {
          filter: groupChannelFilter,
          order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
        },
      )

      collection.setGroupChannelCollectionHandler(channelHandlers)

      collection
        .loadMore()
        .then((channels) => {
          setState((prevState) => ({
            ...prevState,
            channels,
            loading: false,
          }))
          return true
        })
        .catch((error) => {
          console.error(error)
        })
    },
    [],
  )

  useEffect(() => {
    if (currentUser?.userId) {
      sendbirdChat
        .connect(currentUser?.userId.toString() || "")
        .then(async (user) => {
          const unreadCount =
            await sendbirdChat.groupChannel.getTotalUnreadMessageCount()
          setState((prevState) => ({
            ...prevState,
            sendBirdUser: user,
            connectedToChat: true,
            unreadCount: unreadCount || 0,
          }))
          await sendbirdChat.updateCurrentUserInfo({
            nickname: currentUser?.firstName || "",
          })
          loadChannels({})

          // Fetch user metadata
          const existingMetadata = user.metaData as { user_type?: string }

          if (existingMetadata?.user_type) {
            // Metadata exists, update it
            await user.updateMetaData({
              user_type: "HOST",
            })
          } else {
            // Metadata does not exist, create it
            await user.createMetaData({
              user_type: "HOST",
            })
          }
          return true
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [currentUser?.firstName, currentUser?.userId])

  const loadMessages = useCallback(
    ({
      channel,
      messageHandler,
      onCacheResult,
      onApiResult,
    }: LoadMessagesParams) => {
      const messageFilter = new MessageFilter()

      const collection = channel.createMessageCollection({
        filter: messageFilter,
        startingPoint: Date.now(),
        prevResultLimit: 50,
      })

      collection.setMessageCollectionHandler(messageHandler)
      collection
        .initialize(MessageCollectionInitPolicy.CACHE_AND_REPLACE_BY_API)
        .onCacheResult(
          onCacheResult as (
            err: Error | null,
            messages: BaseMessage[] | null,
          ) => void,
        )
        .onApiResult(
          onApiResult as (
            err: Error | null,
            messages: BaseMessage[] | null,
          ) => void,
        )
      return collection
    },
    [],
  )

  const handleOpenChannel = useCallback(
    (channelUrl: string) => {
      if (state.messageCollection && state.messageCollection.dispose) {
        state.messageCollection?.dispose()
      }

      // if (state.currentlyJoinedChannel?.url === channelUrl) {
      //   return null
      // }

      const { channels } = state
      setState({ ...state, messagesLoading: true })
      const channel = channels?.find((ch) => ch.url === channelUrl)
      if (!channel) return null
      channel.markAsRead().catch((error) => console.error(error))

      const onCacheResult = (
        _err: Error | null,
        messages: UserMessage[] | null,
      ) => {
        if (messages) {
          const reversedMessages = messages.slice().reverse()
          setState((prevState) => ({
            ...(stateRef.current ? stateRef.current : prevState),
            currentlyJoinedChannel: channel,
            messages: reversedMessages,
            messagesLoading: false,
          }))
        }
      }

      const onApiResult = (
        _err: Error | null,
        messages: UserMessage[] | null,
      ) => {
        if (messages) {
          const reversedMessages = messages.slice().reverse()
          setState((prevState) => ({
            ...(stateRef.current ? stateRef.current : prevState),
            currentlyJoinedChannel: channel,
            messages: reversedMessages,
            messagesLoading: false,
          }))
        }
      }

      const messageHandlers: MessageCollectionEventHandler = {
        onMessagesAdded: (_context, _channel, messages) => {
          const updatedMessages = [...stateRef.current.messages, ...messages]

          setState({
            ...stateRef.current,
            messages: updatedMessages as UserMessage[],
          })
        },
      }

      const collection = loadMessages({
        channel,
        messageHandler: messageHandlers,
        onCacheResult,
        onApiResult,
      })

      setState((prevState) => ({
        ...prevState,
        messageCollection: collection,
      }))
      return null
    },
    [loadMessages, state],
  )

  const sendMessage = useCallback(
    async (messageValue: string) => {
      if (!state.currentlyJoinedChannel) {
        return null
      }
      state.currentlyJoinedChannel
        .sendUserMessage({
          message: messageValue,
        })
        .onSucceeded(() => {
          displayToast({
            message: "Message sent",
            variant: "success",
          })
          stateRef.current.currentlyJoinedChannel
            ?.markAsRead()
            .catch((error) => console.error(error))
        })
        .onFailed((error) => {
          console.error(error)
          displayToast({
            message: "Message Failed.",
            variant: "error",
          })
        })
      return null
    },
    [state.currentlyJoinedChannel],
  )

  const handleCreateChannel = useCallback(
    (channelName: string, userIdsToInvite: string[]) => {
      createChannel(channelName, userIdsToInvite).catch((error) =>
        console.error(error),
      )
    },
    [],
  )

  const startTyping = useCallback(() => {
    if (state.currentlyJoinedChannel) {
      // eslint-disable-next-line no-void
      void state.currentlyJoinedChannel.startTyping()
    }
  }, [state.currentlyJoinedChannel])

  const endTyping = useCallback(() => {
    if (state.currentlyJoinedChannel) {
      // eslint-disable-next-line no-void
      void state.currentlyJoinedChannel.endTyping()
    }
  }, [state.currentlyJoinedChannel])

  const resetCurrentlyJoinedChannel = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      currentlyJoinedChannel: null,
    }))
  }, [])

  // Memoize the value object
  const providerValue = useMemo(
    () => ({
      ...state,
      loadChannels,
      handleOpenChannel,
      sendMessage,
      handleCreateChannel,
      startTyping,
      endTyping,
      resetCurrentlyJoinedChannel,
    }),
    [
      state,
      loadChannels,
      handleOpenChannel,
      sendMessage,
      handleCreateChannel,
      startTyping,
      endTyping,
      resetCurrentlyJoinedChannel,
    ],
  )

  return (
    <SendbirdContext.Provider value={providerValue}>
      {children}
    </SendbirdContext.Provider>
  )
}

export default SendbirdProvider
